// No staff available / missed call (route /staffnotavailable), with the optional
// callback-contact form. The record shape written to the call register is identical
// to the old client: { callbackcontact: { email, telephone } }.
import { useEffect, useState } from 'react'
import BrandLockup from '../components/BrandLockup'
import { useLocation } from 'react-router-dom'
import { updateCallRealTime } from '../core/callStatus'
import { httpRequest } from '../core/http'
import { t } from '../i18n'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE_RE = /^\+?[0-9 ()-]{6,18}$/

const StaffNotAvailable = () => {
  const { state } = useLocation() as any

  // On a public kiosk (direct-call) there is no keyboard, so the contact form is
  // useless and confusing: show only an informational message instead (#59/#60).
  const isKiosk = state?.isKiosk === true

  const [wantsContact, setWantsContact] = useState<'' | 'Yes' | 'No'>('')
  const [contactMethod, setContactMethod] = useState<'' | 'email' | 'contact_no'>('')
  const [contactValue, setContactValue] = useState('')
  const [error, setError] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    window.history.pushState(null, '', null)
    window.onpopstate = function () {
      window.history.go(1)
    }
    return () => {
      window.onpopstate = null
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const value = contactValue.trim()
    if (contactMethod === 'email' && !EMAIL_RE.test(value)) {
      setError(t.invalidEmail)
      return
    }
    if (contactMethod === 'contact_no' && !PHONE_RE.test(value)) {
      setError(t.invalidPhone)
      return
    }
    setError('')

    const values = {
      email: contactMethod === 'email' ? value : '',
      telephone: contactMethod === 'contact_no' ? value : '',
    }
    // Keep the realtime record for backward compatibility with the old client.
    await updateCallRealTime(state?.call_id, { callbackcontact: values })
    // Route the request to the QR's assigned staff by email (issue #53). Best-effort:
    // the guest gets the confirmation regardless of the notification result.
    if (state?.call_id) {
      httpRequest('callback-request', 'POST', {
        call_id: state.call_id,
        telephone: values.telephone,
        email: values.email,
      })
    }
    setIsSubmitted(true)
  }

  return (
    <div className="cf-page">
      <div className="cf-card">
        <BrandLockup />
        <div className="cf-icon">📵</div>
        <h1 className="cf-title">{t.cannotAssistTitle}</h1>
        <p className="cf-subtitle">{isKiosk ? t.callbackKioskMessage : t.callbackIntro}</p>

        {!isKiosk && wantsContact === '' && (
          <>
            <p className="cf-subtitle">{t.callbackWantContact}</p>
            <div className="cf-radio-group">
              <label className="cf-radio">
                <input type="radio" name="wantsContact" value="Yes" onChange={() => setWantsContact('Yes')} />
                {t.yes}
              </label>
              <label className="cf-radio">
                <input type="radio" name="wantsContact" value="No" onChange={() => setWantsContact('No')} />
                {t.no}
              </label>
            </div>
          </>
        )}

        {wantsContact === 'No' && (
          <p className="cf-subtitle">{t.callbackDeclined}</p>
        )}

        {wantsContact === 'Yes' && !isSubmitted && (
          <>
            <p className="cf-subtitle">{t.callbackPreferredMethod}</p>
            <div className="cf-radio-group">
              <label className={`cf-radio ${contactMethod === 'email' ? 'selected' : ''}`}>
                <input type="radio" name="contactMethod" value="email" onChange={() => setContactMethod('email')} />
                {t.emailLabel}
              </label>
              <label className={`cf-radio ${contactMethod === 'contact_no' ? 'selected' : ''}`}>
                <input type="radio" name="contactMethod" value="contact_no" onChange={() => setContactMethod('contact_no')} />
                {t.phoneLabel}
              </label>
            </div>

            {contactMethod && (
              <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                <div className="cf-field">
                  <label htmlFor="cf-contact">{contactMethod === 'email' ? t.emailLabel : t.phoneLabel}</label>
                  <input
                    id="cf-contact"
                    type={contactMethod === 'email' ? 'email' : 'tel'}
                    inputMode={contactMethod === 'contact_no' ? 'tel' : undefined}
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                  />
                  {error && <span className="cf-error">{error}</span>}
                </div>
                <button type="submit" className="cf-btn cf-btn-primary">
                  {t.send}
                </button>
              </form>
            )}
          </>
        )}

        {isSubmitted && (
          <p className="cf-subtitle">{t.callbackThanks}</p>
        )}
      </div>
    </div>
  )
}

export default StaffNotAvailable
